import store from '@/store';
import { trackEvent } from '@/utils/gtm';
import { Http } from '@pirates/not-morty-biz';
import {
  Drivers,
  NewDriver,
  DriverGetParams,
  AcceptRejectUseCaseIdentifier,
  DriverResponse,
  DriverRequestAction,
  DriverInvitationToken,
  RequestTokenValidation,
} from './driver-management.model';

export class DriverManagementService {
  public async getAll(getParams: DriverGetParams & { refCompanyId: number }) {
    const params = {
      skip: getParams.skip || 0,
      limit: getParams.limit || 10,
      status: getParams.customerId ? undefined : getParams.status,
      customerId: getParams.customerId,
    };

    if (getParams.search) {
      params['search'] = getParams.search;
    }

    return Http.get<Drivers>(`/api/b2b/driver-management/v1/customer/companies/${getParams.refCompanyId}/drivers`, {
      params,
    });
  }

  public async remove({ id, driverId }: { id: number; driverId: string }) {
    return Http.delete(`/api/b2b/driver-management/v1/customer/companies/${id}/drivers/${driverId}`);
  }

  public async invite({ id, drivers }: { id: number; drivers: NewDriver[] }) {
    return Http.post<Drivers>(`/api/b2b/driver-management/v1/customer/companies/${id}/drivers`, drivers);
  }

  public async replyInvitation(body: { action: AcceptRejectUseCaseIdentifier; token: string; businessEmail?: string }) {
    return Http.patch<DriverResponse>(`/api/b2b/driver-management/v1/customer/drivers/me`, body);
  }

  public async getInvitationDetails(refCompanyId: number, invitationToken: string): Promise<DriverResponse> {
    return Http.get<DriverResponse>(`/api/b2b/driver-management/v1/customer/companies/${refCompanyId}/invitations/${invitationToken}`);
  }

  public async deactivate({ refCompanyId, driverId }: { refCompanyId: number; driverId: string }) {
    return Http.patch<Drivers>(`/api/b2b/driver-management/v1/customer/companies/${refCompanyId}/drivers/${driverId}`, {
      action: 'deactivate',
    });
  }

  public async reinviteDriver({ refCompanyId, driverId }: { refCompanyId: number; driverId: string }) {
    return Http.patch<Drivers>(`/api/b2b/driver-management/v1/customer/companies/${refCompanyId}/drivers/${driverId}`, {
      action: 'reinvite',
    });
  }

  public async proceessPendingDrivers({ action, refCompanyId, requestIds }: { action: DriverRequestAction; refCompanyId: number; requestIds?: string[] }) {
    return Http.patch<Drivers>(`/api/b2b/driver-management/v1/customer/companies/${refCompanyId}/requests`, {
      action,
      requestIds,
    });
  }

  public async getInvitationTokens(refCompanyId: number) {
    return Http.get<{ data: DriverInvitationToken[] }>(`/api/b2b/driver-management/v1/customer/companies/${refCompanyId}/tokens`);
  }

  public async generateInvitationToken(refCompanyId: number) {
    return Http.post<DriverInvitationToken>(`/api/b2b/driver-management/v1/customer/companies/${refCompanyId}/tokens`, {});
  }

  public async requestAccess({ refCompanyId, token, message, businessEmail }: { refCompanyId: number; token: string; message?: string; businessEmail?: string }) {
    return Http.post<DriverResponse>(`/api/b2b/driver-management/v1/customer/drivers/requests/me`, { token, refCompanyId, message, businessEmail });
  }

  public async getRequestTokenDetails({ refCompanyId, token }: { refCompanyId: number; token: string }) {
    return Http.get<RequestTokenValidation>(`/api/b2b/driver-management/v1/customer/companies/${refCompanyId}/tokens/${token}`);
  }

  public trackRequestCompletion(statusUpdatedAt: Date | number) {
    const duration = (Date.now() - new Date(statusUpdatedAt).getTime()) / 1000;
    // send the duration of request in seconds
    trackEvent('driver_request_resolved', { pending_duration: duration }, store);
  }

  /**
   * Updates a company's driver by id
   *
   * @param refCompanyId
   * @param driver
   */
  public async updateBusinessEmail({ refCompanyId, driverId, businessEmail }: { refCompanyId: number; driverId: string; businessEmail: string }) {
    return Http.patch<DriverResponse>(`/api/b2b/driver-management/v1/customer/companies/${refCompanyId}/drivers/${driverId}`, {
      action: DriverRequestAction.UPDATE,
      businessEmail: businessEmail,
    });
  }
}

export const driverManagementService = new DriverManagementService();
